import { useCallback, useState } from "react";

// Light/dark theming. Both our design tokens (src/styles/_tokens.scss) and VUD's
// light-dark stylesheet key off <html data-theme="…">, so switching the theme is
// just a matter of writing that attribute. We always write an explicit value
// (never leave it unset) so the two systems stay in lockstep.

export type Theme = "light" | "dark";

const STORAGE_KEY = "theme";

/** The user's persisted choice, or null if they've never chosen. */
export const getStoredTheme = (): Theme | null => {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
};

/** The OS preference, defaulting to light when unknown. */
export const getSystemTheme = (): Theme =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

/** Stored choice wins; otherwise follow the OS. */
export const resolveInitialTheme = (): Theme =>
  getStoredTheme() ?? getSystemTheme();

/** Paint the theme by setting the root attribute (no persistence). */
export const applyTheme = (theme: Theme): void => {
  document.documentElement.setAttribute("data-theme", theme);
};

/** Apply and remember the theme. */
export const setTheme = (theme: Theme): void => {
  applyTheme(theme);
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Ignore storage failures (private mode, disabled cookies, etc.).
  }
};

/**
 * React binding for the theme. Reads the current value from the root attribute
 * (set pre-React by the inline script in index.html) and returns a toggle that
 * flips + persists it.
 */
export const useTheme = (): [Theme, () => void] => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const current = document.documentElement.getAttribute("data-theme");
    if (current === "light" || current === "dark") return current;
    return resolveInitialTheme();
  });

  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      setTheme(next);
      return next;
    });
  }, []);

  return [theme, toggle];
};
